import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { store } from '../store';
import { setEntities, setMe, setSelectedDirector, setSelectedEntity } from '../store/reducers/me';
import { removeStorageItem } from '../utils/localStorage';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
// La base URL è già /api, quindi per auth usiamo /api/auth
const AUTH_BASE = BASE_URL.endsWith('/api')
  ? BASE_URL.replace(/\/api$/, '') + '/api/auth'
  : BASE_URL + '/auth';

const ACCESS_TOKEN_KEY = 'leopart_access_token';
const REFRESH_TOKEN_KEY = 'leopart_refresh_token';

interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  directorIds: string[];
  userIds: string[];
  isDirector: boolean;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  authenticated: boolean;
  initialized: boolean;
}

interface AuthContextType {
  // Keycloak-compatible interface
  keycloak: {
    token: string | null;
    authenticated: boolean;
    tokenParsed: any;
    login: (credentials?: { username: string; password: string }) => Promise<void>;
    logout: () => void;
    updateToken: (minValidity?: number) => Promise<boolean>;
  };
  initialized: boolean;
  user: AuthUser | null;
  // Direct methods
  loginWithCredentials: (email: string, password: string) => Promise<any>;
  /** Accede con l'ID token di Google. Vedi `loginWithGoogle` nel provider. */
  loginWithGoogle: (idToken: string) => Promise<any>;
  logoutUser: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

function parseJwtPayload(token: string): any {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now() + 30000; // 30s buffer
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    token: null,
    refreshToken: null,
    user: null,
    authenticated: false,
    initialized: false,
  });

  const clearAuth = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Pulizia state Redux + localStorage legati all'utente precedente:
    // senza questo, al prossimo login (es. altro direttore) il frontend
    // continua a usare selectedEntity/entities/me del vecchio user →
    // header entity-id puntato alla wrong entity, chiamate rotte.
    removeStorageItem('selectedEntity');
    removeStorageItem('selectedDirector');
    removeStorageItem('me');
    store.dispatch(setSelectedEntity(undefined));
    store.dispatch(setSelectedDirector(undefined));
    store.dispatch(setEntities(undefined));
    store.dispatch(setMe(undefined));
    setState({
      token: null,
      refreshToken: null,
      user: null,
      authenticated: false,
      initialized: true,
    });
  }, []);

  const refreshTokenFn = useCallback(async (refreshToken: string): Promise<boolean> => {
    try {
      const response = await axios.post(`${AUTH_BASE}/refresh`, { refreshToken });
      const { accessToken, refreshToken: newRefreshToken, user } = response.data;

      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

      const payload = parseJwtPayload(accessToken);
      setState(prev => ({
        ...prev,
        token: accessToken,
        refreshToken: newRefreshToken,
        user: {
          id: payload?.sub || user?.id,
          email: user?.email || payload?.email,
          firstName: user?.firstName || payload?.firstName || '',
          lastName: user?.lastName || payload?.lastName || '',
          directorIds: payload?.directorIds || user?.directorIds || [],
          userIds: payload?.userIds || user?.userIds || [],
          isDirector: (payload?.directorIds || user?.directorIds || []).length > 0,
        },
        authenticated: true,
      }));
      return true;
    } catch {
      clearAuth();
      return false;
    }
  }, [clearAuth]);

  // Initialize from localStorage
  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (token && !isTokenExpired(token)) {
      const payload = parseJwtPayload(token);
      setState({
        token,
        refreshToken,
        user: payload ? {
          id: payload.sub,
          email: payload.email,
          firstName: payload.firstName || '',
          lastName: payload.lastName || '',
          directorIds: payload.directorIds || [],
          userIds: payload.userIds || [],
          isDirector: (payload.directorIds || []).length > 0,
        } : null,
        authenticated: true,
        initialized: true,
      });
    } else if (refreshToken) {
      // Try to refresh
      refreshTokenFn(refreshToken).finally(() => {
        setState(prev => ({ ...prev, initialized: true }));
      });
    } else {
      setState(prev => ({ ...prev, initialized: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Apre la sessione con la risposta di una qualsiasi rotta di login.
   *
   * Estratta perché password e Google la condividono per intero: quello che
   * cambia fra i due è solo QUALE endpoint si chiama e con cosa. Duplicarla
   * avrebbe significato due punti in cui ricordarsi di scrivere il token, e
   * uno dei due sarebbe rimasto indietro al primo cambiamento.
   */
  const applySession = (data: any) => {
    const { accessToken, refreshToken, user } = data;

    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

    const payload = parseJwtPayload(accessToken);
    setState(prev => ({
      ...prev,
      token: accessToken,
      refreshToken,
      user: {
        id: payload?.sub || user?.id,
        email: user?.email || payload?.email,
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        directorIds: payload?.directorIds || user?.directorIds || [],
        userIds: payload?.userIds || user?.userIds || [],
        isDirector: (payload?.directorIds || user?.directorIds || []).length > 0,
      },
      authenticated: true,
    }));

    // Sincronizza user.timezone col browser: spostato in SelectedEntityGuard
    // (richiede header entity-id del backend guard, non disponibile al login).

    return data;
  };

  const loginWithCredentials = async (email: string, password: string) => {
    const response = await axios.post(`${AUTH_BASE}/login`, { email, password });

    if (response.data.mustResetPassword) {
      return { mustResetPassword: true, tempToken: response.data.tempToken };
    }

    return applySession(response.data);
  };

  /**
   * Accesso con Google.
   *
   * `idToken` è la credenziale firmata da Google, che qui NON si legge né si
   * conserva: si inoltra al backend, che è l'unico a poterne verificare la
   * firma. Il client non deve fidarsi del suo contenuto — chiunque potrebbe
   * fabbricarne uno con dentro l'email che preferisce.
   *
   * Da qui in poi la sessione è identica a quella con password: stessi token,
   * stesso profilo, stesso selettore di ruolo. Non esiste una "sessione
   * Google" separata.
   */
  const loginWithGoogle = async (idToken: string) => {
    const response = await axios.post(`${AUTH_BASE}/google`, { idToken });
    return applySession(response.data);
  };

  const logoutUser = useCallback(() => {
    const currentRefreshToken = state.refreshToken;
    const currentToken = state.token;
    if (currentRefreshToken && currentToken) {
      axios.post(`${AUTH_BASE}/logout`, { refreshToken: currentRefreshToken }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      }).catch(() => {});
    }
    clearAuth();
  }, [state.token, state.refreshToken, clearAuth]);

  const tokenParsed = state.token ? parseJwtPayload(state.token) : null;

  const keycloakCompat = {
    token: state.token,
    authenticated: state.authenticated,
    tokenParsed,
    login: async (_credentials?: { username: string; password: string }) => {
      // In the new system, login is handled by the Login page directly
      // This is kept for compatibility
    },
    logout: logoutUser,
    updateToken: async (_minValidity?: number): Promise<boolean> => {
      if (!state.token) return false;
      if (!isTokenExpired(state.token)) return false; // Not refreshed (false = still valid)
      if (!state.refreshToken) {
        clearAuth();
        return false;
      }
      return await refreshTokenFn(state.refreshToken);
    },
  };

  return (
    <AuthContext.Provider value={{
      keycloak: keycloakCompat,
      initialized: state.initialized,
      user: state.user,
      loginWithCredentials,
      loginWithGoogle,
      logoutUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
